/**
 * Suggested example answers for founder clarification questions.
 * Deterministic drafts the user can edit — never auto-saved as contract text.
 */

function has(text, re) {
  return re.test(String(text || ""));
}

/**
 * @param {{ question?: string, requestText?: string }} input
 * @returns {{ example: string, label: string }|null}
 */
export function suggestClarificationExample(input = {}) {
  const question = String(input.question || "").trim();
  const request = String(input.requestText || "").trim();
  if (!question) return null;

  const combined = `${question}\n${request}`;

  if (
    has(question, /how long|wait before timing out|timeout.*how|duration|TTL/i) ||
    (has(question, /how long/i) && has(combined, /timeout|too long|forever|slow/i))
  ) {
    return {
      label: "Suggested example",
      example:
        "30 seconds. If a request is still running after that, Fastify should stop it and return the timeout error.",
    };
  }

  if (
    has(question, /all requests|selected routes|which routes|every request|scope/i) &&
    has(combined, /request|route|fastify|timeout|slow/i)
  ) {
    return {
      label: "Suggested example",
      example:
        "Apply to all HTTP requests by default. Long-running routes can opt out only when we explicitly mark them.",
    };
  }

  if (
    has(question, /error response include|timeout error|what should.*(error|response)/i) ||
    (has(question, /include/i) && has(combined, /timeout error|error response/i))
  ) {
    return {
      label: "Suggested example",
      example:
        "Return a clear timeout error (for example HTTP 503 or 408) with a short message that the request timed out, plus enough safe detail for the client to retry — no stack traces or internal internals.",
    };
  }

  if (has(question, /who (?:is|are)|actor|whose|which (?:user|role)/i)) {
    return {
      label: "Suggested example",
      example: has(request, /fastify/i)
        ? "Fastify (the server framework handling the request)."
        : has(request, /\blibrary\b/i)
          ? "The library described in the request."
          : "The system named in the request (edit if a more specific owner should apply).",
    };
  }

  if (has(question, /exact requirement wording|confirm the exact/i)) {
    const snippet = request
      ? request.length > 180
        ? `${request.slice(0, 177)}…`
        : request
      : "";
    return {
      label: "Suggested example",
      example: snippet
        ? `Use this exact wording as the requirement: “${snippet}”`
        : "Use the exact sentence from the original request as the requirement wording.",
    };
  }

  // Generic fallback — still editable, never treated as authoritative.
  return {
    label: "Suggested example",
    example:
      "Spell out the concrete choice you want (number, scope, or response shape) in one or two sentences the engineer can implement.",
  };
}
